/// FOURJS_START_COPYRIGHT(D,2014)
/// Property of Four Js*
/// (c) Copyright Four Js 2014, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("StoredSettingsService", ["InitService"], function (context, cls) {
  /**
   * Declare StoredSettingsService class
   */
  class StoredSettingsService {
    constructor() {
      // Store prefix for forms
      this._formPrefix = "genero_forms";
      this._globalPrefix = "gbc_";
      // Use to handle events
      this._eventListener = new cls.EventListener();

      // Flag to inform that there is no more space available in settings
      this.quotaExceededError = false;

      // Flag to check if storedSettings are enabled
      const storedState = JSON.parse(localStorage.getItem("gbc_storedSettingsEnable"));
      if (storedState === null) {
        this.enable(true);
      }

      this._isEnabled = this.areEnabled();

      window.addEventListener("storage", this._onChange.bind(this)); // binding the class instead of window
    }

    /**
     * Access: read localStorage
     * @param {String} id - key in localStorage
     * @return {any|boolean}
     */
    read(id) {
      if (this._isEnabled && window.localStorage) {
        return JSON.parse(localStorage.getItem(id));
      }
      return false;
    }

    /**
     * Writes to localstorage, bypassing Stored Settings enabled check
     * @param id
     * @param content
     * @private
     */
    _writeStorage(id, content) {
      try {
        localStorage.setItem(id, JSON.stringify(content));
      } catch (e) {
        if (e.name === "QuotaExceededError") {
          console.error(e);
          this.quotaExceededError = true;
          this._eventListener.emit("QuotaExceededError");
        }
      }
    }
    /**
     * Access: writes to localStorage if Stored Settings are enabled
     * @param {String} id - key in localStorage
     * @param {any} content - data to store in localStorage (will be stringified)
     */
    write(id, content) {
      if (!this._isEnabled || !window.localStorage) {
        return;
      }
      this._writeStorage(id, content);
    }

    /**
     * Watch entry to callback when there is a change
     * @param {String} watchedKey
     * @param {function} callback
     * @param {Boolean} once - trigger once then destroy handler
     * @return {*}
     */
    onChange(watchedKey, callback, once = false) {
      return this._eventListener.when("storageEvent", ({ data }) => {
        const [key, newValue] = data;
        if (watchedKey === key) {
          callback(JSON.parse(newValue));
        }
      });
    }

    /**
     * Internal handler for change event
     * @param event
     * @private
     */
    _onChange(event) {
      const { key, newValue } = event;

      this._eventListener.emit("storageEvent", key, newValue);
    }

    /**
     * Handler when no more space available
     * @param {function} callback
     */
    onQuotaExceededError(callback) {
      this._eventListener.when("QuotaExceededError", callback);
    }

    /**
     * Remove entry from localStorage
     * @param {String} id
     */
    remove(id) {
      localStorage.removeItem(id);
    }

    /**
     * Get the list of entries in localStorage
     * @return {string[]}
     */
    keys() {
      return Object.keys(localStorage);
    }

    /**
     * Check if key exists in localStorage
     * @param {String} key
     * @return {boolean}
     */
    hasKey(key) {
      return Object.keys(localStorage).indexOf(key) > -1;
    }

    /**
     * Empty localStorage
     */
    clear() {
      localStorage.clear();
    }

    /**
     *
     * @param {boolean} force Force writing the entry, event if it already exists
     * @private
     */
    _setDefault(force) {
      const defaultSettings = {
        browserDefaultLocale: true,
        locale: null,
        logLevel: "info",
        logTypes: ["_default", "keyboard", "clipboard", "mouse", "ui", "scheduler", "focus", "gICAPI", "networkProtocol", "sw", "input", "ClientQA"],
        accessibilityMode: false,
        sidebarDrawerDocked: null,
        sidebarVisible: false,
        sidebarWidth: 42,
        storedSettingsEnable: this.areEnabled() !== null ? this.areEnabled() : true, // dont reset if exists
        theme: null,
        userLocalStorage: null
      };
      for (const [key, value] of Object.entries(defaultSettings)) {
        if (force || !this.hasKey(this._globalPrefix + key)) {
          this.writeGbcSettings(key, value);
        }
      }
    }

    /**
     * Should be called once Services are ready
     */
    init() {
      this._setDefault(false);
    }

    /**
     * Write GBC specific settings in storage
     * @param {String} key
     * @param {*} value
     */
    writeGbcSettings(key, value) {
      this.write(this._globalPrefix + key, value);
    }

    /**
     * Read GBC specific settings in storage
     * @param {String} key
     * return {*}
     */
    readGbcSettings(key) {
      return this.read(this._globalPrefix + key);
    }

    /**
     * Read User local storage for frontcalls
     * @return {*|boolean}
     */
    readUserLocalStorage() {
      return JSON.parse(localStorage.getItem(`${this._globalPrefix}userLocalStorage`));
    }

    /**
     * Write User local storage for frontcalls
     * @param value
     */
    writeUserLocalStorage(value) {
      this._writeStorage("gbc_userLocalStorage", value);
    }

    /**
     * Read Session variable
     * @param {string} key
     * @return {string}
     */
    readSessionStorage(key) {
      return sessionStorage.getItem(key);
    }

    /**
     * Write session variable
     * @param key
     * @param value
     */
    writeSessionStorage(key, value) {
      sessionStorage.setItem(key, value);
    }

    /**
     * Remove all keys in session storage
     */
    clearSessionStorage() {
      sessionStorage.clear();
    }

    /**
     * Helper to get a cookie by name
     * @param name {string}
     * @returns {*}
     * @private
     */
    _getCookie(name) {
      const match = document.cookie.match(new RegExp(name + "=([^;]+)"));
      if (match) {
        return match[1];
      }
      return null;
    }

    /**
     * Choose to use or not Stored Settings
     * @param {boolean} state is Enable?
     */
    enable(state) {
      // write directly into the storage
      localStorage.setItem(`${this._globalPrefix}storedSettingsEnable`, JSON.stringify(state));
      this._isEnabled = state;
    }

    /**
     * Check if StoredSettings are enabled or not
     * @returns {boolean}
     */
    areEnabled() {
      // read directly into the storage
      return JSON.parse(localStorage.getItem(`${this._globalPrefix}storedSettingsEnable`));
    }

    /**
     * Erase all GBC and genero form related items
     * from local storage
     * And write default GBC values
     */
    reset() {
      // Remove Keys that starts by "gbc_" or by "genero_form"
      for (const key of this.keys()) {
        if (key.startsWith(this._formPrefix)) {
          this.remove(key);
        }
      }
      this._setDefault(true);

      this._eventListener.emit("storedSettingsReset");
    }

    /**
     * Default initializer for form
     * @param formName
     * @return {any|boolean}
     * @private
     */
    _initFormSettings(formName) {
      this.write(`${this._formPrefix}.${formName}`, {
        layoutContainer: [],
        tables: [],
        modal: [],
        groupCollapsed: []
      });
      return this.getFormSettings(formName);
    }

    /**
     * Get Settings for a given form
     * @param {String} formName - identifier of the form
     * @return {*|boolean}
     */
    getFormSettings(formName) {
      return (
        this.read(`${this._formPrefix}.${formName}`) || {
          layoutContainer: [],
          tables: [],
          modal: [],
          groupCollapsed: []
        }
      );
    }

    /**
     * Set Settings of a given form
     * @param {String} formName - identifier of the form
     * @param {*} settings - data to store
     */
    setFormSettings(formName, settings) {
      this.write(`${this._formPrefix}.${formName}`, settings);
    }

    //Sidebar related functions
    /**
     * Store the sidebar status: visible / hidden
     * @param visible {bool}
     */
    saveSideBarVisible(visible) {
      this.writeGbcSettings(context.constants.storedSettingKeys.sidebarVisible, visible);
    }

    /**
     * Check if sideBar is visible
     * @returns {bool}
     */
    isSideBarVisible() {
      return this.readGbcSettings(context.constants.storedSettingKeys.sidebarVisible) || false;
    }

    //Accessibility mode related functions
    /**
     * Persist the user-facing accessibility mode toggle.
     * @param {boolean} enabled
     */
    writeAccessibilityMode(enabled) {
      this.writeGbcSettings(context.constants.storedSettingKeys.accessibilityMode, Boolean(enabled));
    }

    /**
     * Read the persisted accessibility mode toggle.
     * @returns {boolean}
     */
    readAccessibilityMode() {
      return Boolean(this.readGbcSettings(context.constants.storedSettingKeys.accessibilityMode));
    }

    /**
     * Store the sidebar width
     * @param width {number}
     */
    saveSideBarWidth(width) {
      this.writeGbcSettings(context.constants.storedSettingKeys.sidebarWidth, width);
    }

    /**
     * Get the sidebar width
     * @returns {number}
     */
    loadSideBarWidth() {
      return this.readGbcSettings(context.constants.storedSettingKeys.sidebarWidth);
    }

    //Language related functions
    /**
     * Manually set the language of the interface
     * @param {string} locale should be something like "en-US" or "fr-FR"
     */
    setLanguage(locale) {
      this.saveLocale(locale);
      gbc.I18NService.setLng(locale);
    }

    /**
     * @deprecated Use readLanguage() instead
     */
    loadLanguage() {
      return this.readLanguage();
    }

    /**
     * Get the language set
     * @returns {string} locale
     */
    readLanguage() {
      let locale = this.readLocale();
      if (!locale) {
        locale = this._getCookie("lang");
      }
      return locale;
    }

    /**
     * Save the fact that settings use the browser's default locale
     * @param {boolean} useBrowser
     */
    useBrowserDefaultLocale(useBrowser) {
      this.writeGbcSettings(context.constants.storedSettingKeys.browserDefaultLocale, useBrowser);
    }

    /**
     * Check if the locale use the browser's default locale
     * @param useBrowser
     * @return {*|boolean}
     */
    isBrowserDefaultLocale(useBrowser) {
      return this.readGbcSettings(context.constants.storedSettingKeys.browserDefaultLocale);
    }

    //Log level related functions
    /**
     * Store the log level
     * @param {string} logLevel
     */
    saveLogLevel(logLevel) {
      this.writeGbcSettings("logLevel", logLevel);
    }

    /**
     * Get the log level
     * @returns {string} logLevel
     */
    loadLogLevel() {
      return this.readGbcSettings("logLevel") || "info";
    }

    //Log types related functions
    /**
     * store the log types
     * @param {String[]} logTypes
     */
    saveLogTypes(logTypes) {
      this.writeGbcSettings("logTypes", logTypes);
    }

    /**
     * Get the log types
     * @returns {string} logTypes
     */
    loadLogTypes() {
      return this.readGbcSettings("logTypes");
    }

    /**
     * @deprecated Use writeTheme() instead
     */
    saveTheme(themeName) {
      this.writeTheme(themeName);
    }

    /**
     * Store theme in localStorage
     * @param {string} themeName The theme to store
     */
    writeTheme(themeName) {
      this.writeGbcSettings("theme", themeName);
    }

    /**
     * @deprecated Use readTheme() instead
     */
    loadTheme() {
      return this.readTheme();
    }

    /**
     * Get stored Theme
     * @return {string|boolean}
     */
    readTheme() {
      return this.readGbcSettings("theme");
    }

    /**
     * Store current locale
     * @param {string} localeName
     */
    saveLocale(localeName) {
      this.writeGbcSettings("locale", localeName);
    }

    /**
     * Get stored locale
     * @return {string|boolean}
     * @deprecated Use readLocale() instead
     */
    loadLocale() {
      return this.readLocale();
    }

    /**
     * Get stored locale
     * @return {string|boolean}
     */
    readLocale() {
      return this.readGbcSettings("locale");
    }

    // Tables related functions
    /**
     * Set Table Stored settings
     * @param {string} formName - name of the current form
     * @param {string} tableName - name of the table
     * @param {'sortType' | 'sortColumn'} propertyName of the table setting to set
     * @param propertyValue
     */
    saveTableSettings(formName, tableName, propertyName, propertyValue) {
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      let tableIndex = formSettings?.tables.findIndex((item) => item._id === tableName);

      if (tableIndex < 0) {
        formSettings.tables.push(this._getTableDefaultSettings(formName, tableName));
        tableIndex = this._findTableIndex(tableName, formSettings);
      }
      formSettings.tables[tableIndex][propertyName] = propertyValue;
      this.setFormSettings(formName, formSettings);
    }

    /**
     * Get Table Stored settings
     * @param {string} formName - name of the current form
     * @param {string} tableName - name of the table
     * @param {'sortType' | 'sortColumn'} propertyName of the table setting to get
     * @return {*}
     */
    loadTableSettings(formName, tableName, propertyName) {
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      const tableIndex = formSettings?.tables.findIndex((item) => item._id === tableName);

      if (tableIndex < 0) {
        return null;
      }

      const value = this.getFormSettings(formName).tables[tableIndex][propertyName];
      return typeof value !== "undefined" ? value : null;
    }

    /**
     * Generate default Table settings
     * @param {String} formName
     * @param {String} tableName
     * @return {{sortColumn: number, sortType: string, columns: {}, _id}}
     * @private
     */
    _getTableDefaultSettings(formName, tableName) {
      return {
        _id: tableName,
        sortColumn: 0,
        sortType: "desc",
        sortSpec: "",
        columns: {}
      };
    }

    /**
     * Helper to get index of the table in storedSettings table array
     * @param {String} tableName
     * @param {Object} formSettings
     * @return {number | undefined}
     * @private
     */
    _findTableIndex(tableName, formSettings) {
      return formSettings?.tables.findIndex((item) => item._id === tableName);
    }

    /**
     * Set a column settings for a given table
     * @param {string} formName - name of the current form
     * @param {string} tableName - name of the table
     * @param {number} columnIndex - index of the column
     * @param {('order'|'width'|'hidden')} key to set
     * @param {*} data - payload to store
     */
    saveTableColumnSettings(formName, tableName, columnIndex, key, data) {
      // First get form settings or init it
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      // Then get Table in settings
      let tableIndex = this._findTableIndex(tableName, formSettings);

      // If no table found in storage, create it!
      if (tableIndex < 0) {
        formSettings.tables.push(this._getTableDefaultSettings(formName, tableName));
        tableIndex = this._findTableIndex(tableName, formSettings);
      }

      // Get current Column info
      const columnInfo = formSettings.tables[tableIndex].columns[columnIndex];

      // Update it
      formSettings.tables[tableIndex].columns[columnIndex] = {
        ...columnInfo,
        [key]: data.value
      };

      // Save it
      this.setFormSettings(formName, formSettings);
    }

    /**
     * Get the Table's given column settings
     * @param {string} formName - name of the current form
     * @param {string} tableName - name of the table
     * @param {number} columnIndex - index of the column
     * @param {('order'|'width'|'hidden')} key to set
     * @return {*} payload from store
     */
    loadTableColumnSettings(formName, tableName, columnIndex, key) {
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      const tableIndex = this._findTableIndex(tableName, formSettings);
      const value = this.getFormSettings(formName).tables[tableIndex]?.columns[columnIndex]?.[key];
      return typeof value !== "undefined" ? value : null;
    }

    /**
     * Reset settings of a given table
     * @param formName
     * @param tableName
     * @param {('sortColumn'|'sortType'|'all')} settingName
     */
    resetTableSettings(formName, tableName, settingName) {
      // First get form settings or init it
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      // Then get Table in settings
      let tableIndex = this._findTableIndex(tableName, formSettings);
      if (tableIndex >= 0) {
        if (settingName === "all") {
          formSettings.tables.splice(tableIndex, 1);
        } else {
          formSettings.tables[tableIndex].sortType = null;
          formSettings.tables[tableIndex].sortColumn = null;
          formSettings.tables[tableIndex].sortSpec = null;
        }
      }
      this.setFormSettings(formName, formSettings);
    }

    //Splitter related functions
    /**
     * A Splitter information.
     * @typedef {Object} SplitterInfo
     * @property {Array<number>} horizontal - The horizontal split data
     * @property {Array<number>} vertical - The vertical split data
     * @property {String?} _id - The split unique identifier
     */

    /**
     * Define a splitter according to parameters
     * @param formName
     * @param identifier
     * @param {SplitterInfo} splitInfo
     */
    saveSplitter(formName, identifier, splitInfo) {
      const { horizontal, vertical } = splitInfo;
      // search in splitter data of form
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      const splitIndex = formSettings.layoutContainer.findIndex((item) => item._id === identifier);

      const splitInfoItem = {
        _id: identifier,
        horizontal,
        vertical
      };

      if (splitIndex < 0) {
        // append
        formSettings.layoutContainer.push(splitInfoItem);
      } else {
        // edit
        formSettings.layoutContainer[splitIndex] = splitInfoItem;
      }
      this.setFormSettings(formName, formSettings);
    }

    /**
     * Get Splitter info
     * @param formName
     * @param identifier
     * @returns {SplitterInfo}
     */
    loadSplitter(formName, identifier) {
      if (!this.areEnabled()) {
        return false;
      }
      const formSettings = this.getFormSettings(formName);
      return formSettings?.layoutContainer.find((item) => item._id === identifier);
    }

    //Collapsible group related functions
    /**
     * Define a group collapsed state according to parameters
     * @param {string} formName
     * @param {string} identifier
     * @param {boolean} collapsedInfo
     */
    saveGroupCollapsedState(formName, identifier, collapsedInfo) {
      // search in group data of form
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      const groupIndex = formSettings.groupCollapsed.findIndex((item) => item._id === identifier);

      const groupCollapsedItem = {
        _id: identifier,
        collapsed: collapsedInfo
      };

      if (groupIndex < 0) {
        // append
        formSettings.groupCollapsed.push(groupCollapsedItem);
      } else {
        // edit
        formSettings.groupCollapsed[groupIndex] = groupCollapsedItem;
      }
      this.setFormSettings(formName, formSettings);
    }

    /**
     * Get group collapsed state
     * @param {string} formName
     * @param {string} identifier
     * @returns {boolean|null} true if set as collapsed false if not, undefined if never defined in settings
     */
    loadGroupCollapsedState(formName, identifier) {
      const formSettings = this.getFormSettings(formName);
      const group = formSettings?.groupCollapsed.find((item) => item._id === identifier);
      return group?.collapsed;
    }

    /**
     * Internal search modal index in stored settings list
     * @param {String} appId
     * @param {String} formName
     * @param {String} windowName
     * @param {Object} formSettings
     * @return {*}
     * @private
     */
    _findModalIndex(appId, formName, windowName, formSettings) {
      return formSettings?.modal.findIndex((item) => item.appId === appId && item.windowName === windowName);
    }

    /**
     * Create a default settings object for modal
     * @param {String} appId
     * @param {String} formName
     * @param {String} windowName
     * @return {{appId, formName, windowName, position: {}, dimension: {}}}
     * @private
     */
    _getModalDefaultSettings(appId, formName, windowName) {
      return {
        appId,
        formName,
        windowName,
        position: {},
        dimension: {}
      };
    }

    /**
     * Save modal Settings
     * @param {String} appId
     * @param {String} formName
     * @param {String} windowName
     * @param {Object} modalSettingsData : should contain dimension and/or position
     */
    saveModalSettings(appId, formName, windowName, modalSettingsData) {
      // First get form settings or init it
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      // Then get Table in settings
      let modalIndex = this._findModalIndex(appId, formName, windowName, formSettings);

      // If no table found in storage, create it!
      if (modalIndex < 0) {
        formSettings.modal.push(this._getModalDefaultSettings(appId, formName, windowName));
        modalIndex = this._findModalIndex(appId, formName, windowName, formSettings);
      }

      // Get current Column info
      const currentModalInfo = formSettings.modal[modalIndex];
      // use current value by default
      const { position = currentModalInfo.position, dimension = currentModalInfo.dimension } = modalSettingsData;

      // Update it
      formSettings.modal[modalIndex] = {
        ...currentModalInfo,
        position,
        dimension
      };

      // Save it
      this.setFormSettings(formName, formSettings);
    }

    /**
     * Load modal Settings from storage
     * @param {String} appId
     * @param {String} formName
     * @param {String} windowName
     * @param {String} key
     * @return {Object} modalSettingsData : should contain dimension and/or position
     */
    loadModalSettings(appId, formName, windowName, key) {
      // First get form settings or init it
      const formSettings = this.getFormSettings(formName) || this._initFormSettings(formName);
      // Then get Table in settings
      let modalIndex = this._findModalIndex(appId, formName, windowName, formSettings);

      // If no table found in storage, create it!
      if (modalIndex < 0) {
        formSettings.modal.push(this._getModalDefaultSettings(appId, formName, windowName));
        modalIndex = this._findModalIndex(appId, formName, windowName, formSettings);
      }

      // Get current Modal info
      return this.getFormSettings(formName).modal[modalIndex]?.[key] || null;
    }

    /**
     * Handling a callback on reset storedSettings
     * @param hook
     * @returns {*|HandleRegistration}
     */
    whenReset(hook) {
      return this._eventListener.when("storedSettingsReset", hook);
    }
  }

  /**
   * Stored Settings Service to handle clientside specific configurations
   * @class gbc.StoredSettingsService
   * @gbcService
   */
  gbc.StoredSettingsService = new StoredSettingsService();

  context.InitService.register(context.StoredSettingsService);
});
